import type {
  AdminCareerRequirement,
  AdminContentItem,
  AdminContentKind,
  AdminContentLocale,
  AdminUser,
  AdminCooperationItem,
  CreateAdminUserPayload,
} from '../contentConsoleApi';

export type ConsoleLocale = AdminContentLocale;

export type CareerRequirementForm = AdminCareerRequirement;
export type CooperationItemForm = AdminCooperationItem;

export type ContentForm = {
  id?: number;
  group_key: string;
  language: ConsoleLocale;
  slug: string;
  badge: string;
  title: string;
  summary: string;
  body: string;
  news_topics: string[];
  resource_category: 'talent' | 'experts' | 'service-cases';
  service_page_slot: string;
  requirements: CareerRequirementForm[];
  cooperation_items: CooperationItemForm[];
  questionnaire_title: string;
  questionnaire_description: string;
  contact_phone: string;
  contact_email: string;
  qr_hint: string;
  published_at: string;
  is_published: boolean;
  is_pinned: boolean;
  is_default_display: boolean;
  sort_order: number;
  available_languages: ConsoleLocale[];
  cover_image_url?: string | null;
  coverFile?: File | null;
  clear_cover_image: boolean;
  qr_image_url?: string | null;
  qrImageFile?: File | null;
  clear_qr_image: boolean;
};

export type SelfForm = {
  username: string;
  display_name: string;
};

export type PasswordForm = {
  current_password: string;
  new_password: string;
  confirm_password: string;
};

export type ManagedPasswordForm = {
  new_password: string;
  confirm_password: string;
};

export type ManagedUserForm = CreateAdminUserPayload & {
  id?: number;
  is_superuser: boolean;
};

export const NEWS_TOPICS = [
  { value: 'policy', label: '各国政策' },
  { value: 'events', label: '近期事件' },
  { value: 'seminars', label: '研讨会' },
  { value: 'hr-guide', label: '出海 HR 宝典' },
] as const;

export const RESOURCE_CATEGORIES = [
  { value: 'talent', label: '人才库' },
  { value: 'experts', label: '专家库' },
  { value: 'service-cases', label: '服务案例库' },
] as const;

export const SERVICE_PAGE_SLOT_OPTIONS = [
  { value: '', label: '不在服务页展示' },
  { value: 'project-development', label: '项目开发定制化' },
  { value: 'product-incubation', label: '产品孵化定制化' },
  { value: 'entrepreneurship-activity', label: '创业活动定制化' },
  { value: 'industry-upgrade', label: '产业升级定制化' },
] as const;

export const LOCALES: Array<{ value: ConsoleLocale; label: string }> = [
  { value: 'zh', label: '中文' },
  { value: 'en', label: 'English' },
  { value: 'ja', label: '日本語' },
];

const DEFAULT_BADGES: Record<AdminContentKind, Record<ConsoleLocale, string>> = {
  news: { zh: '平台动态', en: 'Platform Update', ja: 'プラットフォーム更新' },
  resource: { zh: '资源中心', en: 'Resource Center', ja: 'リソースセンター' },
  career: { zh: '热招岗位', en: 'Open Position', ja: '募集中ポジション' },
  'project-cooperation': { zh: '项目合作', en: 'Project Cooperation', ja: 'プロジェクト協業' },
};

const createEmptyRequirement = (index: number): CareerRequirementForm => ({
  index: String(index).padStart(2, '0'),
  title: '',
  description: '',
});

export const createCooperationItemForm = (index: number): CooperationItemForm => ({
  index: String(index).padStart(2, '0'),
  title: '',
  description: '',
});

export const emptyContent = (kind: AdminContentKind, language: ConsoleLocale): ContentForm => ({
  group_key: '',
  language,
  slug: '',
  badge: DEFAULT_BADGES[kind][language],
  title: '',
  summary: '',
  body: '',
  news_topics: [],
  resource_category: 'talent',
  service_page_slot: '',
  requirements: kind === 'career' ? [createEmptyRequirement(1)] : [],
  cooperation_items: kind === 'project-cooperation' ? [createCooperationItemForm(1)] : [],
  questionnaire_title: '',
  questionnaire_description: '',
  contact_phone: '',
  contact_email: '',
  qr_hint: '',
  published_at: new Date().toISOString().slice(0, 10),
  is_published: true,
  is_pinned: false,
  is_default_display: false,
  sort_order: 0,
  available_languages: [language],
  cover_image_url: null,
  coverFile: null,
  clear_cover_image: false,
  qr_image_url: null,
  qrImageFile: null,
  clear_qr_image: false,
});

export const emptyManagedUser = (): ManagedUserForm => ({
  username: '',
  display_name: '',
  password: '',
  is_active: true,
  is_superuser: false,
  can_manage_news: false,
  can_manage_resources: false,
  can_manage_careers: false,
  can_manage_project_cooperation: false,
  can_manage_analytics: false,
  can_manage_users: false,
});

export const mapContent = (item: AdminContentItem): ContentForm => ({
  id: item.id,
  group_key: item.group_key,
  language: item.language,
  slug: item.slug,
  badge: item.badge,
  title: item.title,
  summary: item.summary,
  body: item.body,
  news_topics: item.news_topics ?? [],
  resource_category: item.resource_category ?? 'talent',
  service_page_slot: item.service_page_slot ?? '',
  requirements: item.requirements?.length ? item.requirements : [],
  cooperation_items: item.cooperation_items?.length ? item.cooperation_items : [],
  questionnaire_title: item.questionnaire_title ?? '',
  questionnaire_description: item.questionnaire_description ?? '',
  contact_phone: item.contact_phone ?? '',
  contact_email: item.contact_email ?? '',
  qr_hint: item.qr_hint ?? '',
  published_at: item.published_at,
  is_published: item.is_published,
  is_pinned: item.is_pinned,
  is_default_display: item.is_default_display ?? false,
  sort_order: item.sort_order,
  available_languages: item.available_languages,
  cover_image_url: item.cover_image_url ?? null,
  coverFile: null,
  clear_cover_image: false,
  qr_image_url: item.qr_image_url ?? null,
  qrImageFile: null,
  clear_qr_image: false,
});

export const mapUser = (user: AdminUser): ManagedUserForm => ({
  id: user.id,
  username: user.username,
  display_name: user.display_name,
  password: '',
  is_active: user.is_active,
  is_superuser: user.is_superuser,
  can_manage_news: user.permissions.can_manage_news,
  can_manage_resources: user.permissions.can_manage_resources,
  can_manage_careers: user.permissions.can_manage_careers,
  can_manage_project_cooperation: user.permissions.can_manage_project_cooperation,
  can_manage_analytics: user.permissions.can_manage_analytics,
  can_manage_users: user.permissions.can_manage_users,
});

export const allowedKinds = (user: AdminUser | null): AdminContentKind[] => {
  if (!user) return [];
  if (user.is_superuser) return ['news', 'resource', 'career', 'project-cooperation'];

  const kinds: AdminContentKind[] = [];
  if (user.permissions.can_manage_news) kinds.push('news');
  if (user.permissions.can_manage_resources) kinds.push('resource');
  if (user.permissions.can_manage_careers) kinds.push('career');
  if (user.permissions.can_manage_project_cooperation) kinds.push('project-cooperation');
  return kinds;
};

export const createRequirementForm = (index: number) => createEmptyRequirement(index);

export const normalizeRequirementIndices = (requirements: CareerRequirementForm[]) =>
  requirements.map((requirement, index) => ({
    ...requirement,
    index: requirement.index || String(index + 1).padStart(2, '0'),
  }));

export const normalizeCooperationIndices = (items: CooperationItemForm[]) =>
  items.map((item, index) => ({
    ...item,
    index: item.index || String(index + 1).padStart(2, '0'),
  }));

export const buildPayload = (kind: AdminContentKind, form: ContentForm) => {
  const data = new FormData();
  ['group_key', 'language', 'slug', 'badge', 'title', 'summary', 'body', 'published_at'].forEach((key) => {
    data.append(key, String(form[key as keyof ContentForm] ?? ''));
  });
  data.append('is_published', String(form.is_published));
  data.append('is_pinned', String(form.is_pinned));
  data.append('sort_order', String(form.sort_order));
  data.append('clear_cover_image', String(form.clear_cover_image));
  if (form.coverFile) data.append('cover_image', form.coverFile);
  if (kind === 'news') form.news_topics.forEach((topic) => data.append('news_topics', topic));
  if (kind === 'resource') {
    data.append('resource_category', form.resource_category);
    data.append('service_page_slot', form.resource_category === 'service-cases' ? form.service_page_slot : '');
  }
  if (kind === 'career') {
    data.append('is_default_display', String(form.is_default_display));
    data.append('requirements', JSON.stringify(normalizeRequirementIndices(form.requirements)));
    data.append('contact_phone', form.contact_phone);
    data.append('contact_email', form.contact_email);
    data.append('qr_hint', form.qr_hint);
    data.append('clear_qr_image', String(form.clear_qr_image));
    if (form.qrImageFile) data.append('qr_image', form.qrImageFile);
  }
  if (kind === 'project-cooperation') {
    data.append('is_default_display', String(form.is_default_display));
    data.append('cooperation_items', JSON.stringify(normalizeCooperationIndices(form.cooperation_items)));
    data.append('questionnaire_title', form.questionnaire_title);
    data.append('questionnaire_description', form.questionnaire_description);
    data.append('qr_hint', form.qr_hint);
    data.append('clear_qr_image', String(form.clear_qr_image));
    if (form.qrImageFile) data.append('qr_image', form.qrImageFile);
  }
  return data;
};
